import React, { useState, useRef, useEffect } from 'react';
import { GlobalWorkerOptions } from 'pdfjs-dist';
import 'pdfjs-dist/web/pdf_viewer.css';
import { Root, Pages, Page, CanvasLayer, TextLayer, AnnotationLayer, HighlightLayer, Thumbnails, Thumbnail, usePdf, usePdfJump, SelectionTooltip, TotalPages, useSelectionDimensions, } from '@anaralabs/lector';
import { Download, Maximize, Minimize, RotateCwSquare, PanelRight, PanelLeft, Search as SearchIcon, Sun, Moon, Quote, AlertCircle, } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { useSourceStore } from 'store/source.store';
import { useChatStore } from 'store/chat.store';
import { http } from 'services';
import config from 'config';
import { SearchUI } from './PdfSearch';
import PDFZoomDropdown from './PdfZoomDropdown';
// Configure PDF.js worker - must match installed pdfjs-dist version (4.10.38)
GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@4.10.38/build/pdf.worker.min.mjs`;

// Ensure file URL is absolute (simplified like efftime-frontend)
const ensureAbsoluteFileUrl = (fileUrl: string): string => {
    if (!fileUrl)
        return '';
    if (/^https?:\/\//i.test(fileUrl)) {
        return fileUrl;
    }
    const base = config.api.base_url;
    const path = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
    return `${base}${path}`;
};
interface PdfViewerProps {
    fileUrl: string;
    documentId?: string;
    className?: string;
}
// Custom page input component with dynamic width
const CustomPageInput: React.FC<{
    className?: string;
}> = ({ className }) => {
    const currentPage = usePdf((state) => state.currentPage);
    const pdfDocumentProxy = usePdf((state) => state.pdfDocumentProxy);
    const totalPages = pdfDocumentProxy?.numPages || 1;
    const { jumpToPage } = usePdfJump();
    const [inputValue, setInputValue] = useState<string>('');
    useEffect(() => {
        if (currentPage) {
            setInputValue(currentPage.toString());
        }
    }, [currentPage]);
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
    };
    const handleBlur = () => {
        const pageNum = parseInt(inputValue, 10);
        if (!Number.isNaN(pageNum) && pageNum > 0 && pageNum <= totalPages) {
            jumpToPage(pageNum);
        }
        else {
            setInputValue(currentPage?.toString() || '1');
        }
    };
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleBlur();
        }
    };
    return (<input type="text" value={inputValue} onChange={handleChange} onBlur={handleBlur} onKeyDown={handleKeyDown} className={className} style={{
            width: `${Math.max(2, totalPages.toString().length + 0.5)}em`,
            textAlign: 'center',
        }}/>);
};
// Custom selection tooltip with zoom-aware positioning
const CustomSelect: React.FC<{
    text: string;
}> = ({ text }) => {
    const { t } = useTranslation();
    const zoom = usePdf((state) => state.zoom);
    const setHighlightedText = useChatStore((state) => state.setHighlightedText);
    const handleQuote = () => {
        if (text.trim()) {
            setHighlightedText(text.trim());
        }
    };
    return (<SelectionTooltip>
      <div className="dark:hue-rotate-180 dark:invert-[92.5%]" style={{
            transform: `scale(${1 / zoom})`,
            transformOrigin: 'top left',
            position: 'relative',
            top: '-10px',
            left: '60px',
            pointerEvents: 'auto',
        }}>
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-1 flex gap-1">
          <Button size="sm" variant="ghost" onClick={handleQuote} className="flex items-center gap-1 text-xs">
            <Quote className="h-3 w-3"/>{t("quote_in_chat")}</Button>
        </div>
      </div>
    </SelectionTooltip>);
};
// PDF Pages Wrapper with all features
const PDFPagesWrapper: React.FC<{
    showThumbnails: boolean;
    showSearch: boolean;
    rotation: number;
    pdfTheme: 'light' | 'dark';
}> = ({ showThumbnails, showSearch, rotation, pdfTheme }) => {
    const selectionDimensions = useSelectionDimensions();
    const [selectedText, setSelectedText] = useState<string>('');
    const [pageOffset, setPageOffset] = useState<number>(0);
    useEffect(() => {
        const handleSelectionChange = () => {
            if (!selectionDimensions)
                return;
            const selection = selectionDimensions.getSelection();
            if (selection && !selection.isCollapsed) {
                setSelectedText(selection.text);
            }
            else {
                setSelectedText('');
            }
        };
        document.addEventListener('selectionchange', handleSelectionChange);
        return () => {
            document.removeEventListener('selectionchange', handleSelectionChange);
        };
    }, [selectionDimensions]);
    return (<div className={cn('relative grid min-h-0 flex-grow transition-all duration-300', pdfTheme === 'dark' ? 'bg-gray-800/50' : 'bg-white', showThumbnails && showSearch
            ? 'grid-cols-[16rem,16rem,1fr]'
            : showThumbnails
                ? 'grid-cols-[16rem,0,1fr]'
                : showSearch
                    ? 'grid-cols-[0,16rem,1fr]'
                    : 'grid-cols-[0,0,1fr]')}>
      {/* Thumbnails sidebar */}
      <div className={cn('overflow-x-hidden overflow-y-auto', showThumbnails ? 'border-r' : '')}>
        <div className="overflow-x-hidden">
          <Thumbnails className="flex flex-col items-center gap-4 py-4 dark:border-gray-700">
            <Thumbnail className="w-48 transition-all hover:shadow-lg hover:outline hover:outline-gray-300 dark:hover:outline-gray-600" style={{ transform: `rotate(${rotation}deg)` }}/>
          </Thumbnails>
        </div>
      </div>

      {/* Search sidebar */}
      <div className="overflow-x-hidden overflow-y-auto dark:border-gray-700">
        {showSearch && <SearchUI />}
      </div>

      {/* PDF pages */}
      <Pages className={pdfTheme === 'dark'
            ? 'hue-rotate-180 brightness-[80%] contrast-[228%] invert-[92.5%]'
            : undefined} style={{ position: 'relative' }} gap={20} virtualizerOptions={{
            overscan: 5,
        }} initialOffset={pageOffset} onOffsetChange={(offset) => {
            setPageOffset(offset);
        }}>
        <Page style={{
            transform: `rotate(${rotation}deg)`,
            position: 'relative',
        }}>
          <div style={{
            position: 'absolute',
            zIndex: 100,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
        }}>
            {selectedText && <CustomSelect text={selectedText}/>}
          </div>
          <CanvasLayer background="transparent" style={{ zIndex: 1 }}/>
          <TextLayer style={{ zIndex: 2 }}/>
          <AnnotationLayer style={{ zIndex: 3 }}/>
          <HighlightLayer className="rounded-[2px] bg-green-500/20 dark:bg-green-500/30" style={{ zIndex: 4 }}/>
        </Page>
      </Pages>
    </div>);
};
// Main PDF Viewer component
export const PdfViewer: React.FC<PdfViewerProps> = ({ fileUrl, documentId, className }) => {
    const { t } = useTranslation();
    const [showThumbnails, setShowThumbnails] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [pdfTheme, setPdfTheme] = useState<'light' | 'dark'>('light');
    const [loadError, setLoadError] = useState<string | null>(null);
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [isLoadingPdf, setIsLoadingPdf] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const blobUrlRef = useRef<string | null>(null);
    const { setDocumentId } = useSourceStore();
    // Fetch PDF with auth headers and create blob URL
    useEffect(() => {
        if (!fileUrl) {
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
            setBlobUrl(null);
            return;
        }
        // Guard against React StrictMode's dev double-invoke and rapid fileUrl
        // changes: a superseded load must not create/keep a blob, otherwise its
        // object URL gets revoked while the PDF renderer is still fetching it
        // (the failing `blob:` request).
        let cancelled = false;
        const loadPdf = async () => {
            setIsLoadingPdf(true);
            setLoadError(null);
            try {
                const needsAuth = fileUrl.startsWith('/api/') || fileUrl.includes('/api/documents/');
                let blob: Blob;
                if (needsAuth) {
                    const apiPath = fileUrl.startsWith('/api/') ? fileUrl : `/api/${fileUrl}`;
                    console.log('PDF Viewer - Loading PDF via API:', apiPath);
                    const response = await http.request.get(apiPath, { responseType: 'blob' });
                    blob = response.data;
                }
                else {
                    const absoluteUrl = ensureAbsoluteFileUrl(fileUrl);
                    console.log('PDF Viewer - Loading PDF from public URL:', absoluteUrl);
                    const response = await fetch(absoluteUrl);
                    if (!response.ok) {
                        throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
                    }
                    blob = await response.blob();
                }
                if (cancelled) {
                    return;
                }
                if (!blob.type.includes('pdf') && !blob.type.includes('application/octet-stream')) {
                    console.warn('Unexpected content type:', blob.type);
                }
                if (blobUrlRef.current) {
                    URL.revokeObjectURL(blobUrlRef.current);
                }
                const newBlobUrl = URL.createObjectURL(blob);
                console.log('PDF Viewer - Created blob URL:', newBlobUrl);
                blobUrlRef.current = newBlobUrl;
                setBlobUrl(newBlobUrl);
            }
            catch (error: any) {
                if (cancelled) {
                    return;
                }
                console.error('PDF Viewer - Error loading PDF:', error);
                setLoadError(error.message || 'Failed to load PDF. Please verify the file URL is accessible.');
                if (blobUrlRef.current) {
                    URL.revokeObjectURL(blobUrlRef.current);
                    blobUrlRef.current = null;
                }
                setBlobUrl(null);
            }
            finally {
                if (!cancelled) {
                    setIsLoadingPdf(false);
                }
            }
        };
        loadPdf();
        return () => {
            cancelled = true;
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileUrl]);
    useEffect(() => {
        if (documentId) {
            setDocumentId(documentId);
        }
    }, [documentId, setDocumentId]);
    useEffect(() => {
        const handleFullScreenChange = () => {
            setIsFullscreen(Boolean(document.fullscreenElement));
        };
        document.addEventListener('fullscreenchange', handleFullScreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullScreenChange);
        };
    }, []);
    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen().catch((error) => {
                console.error(`Error attempting to enable full-screen mode: ${error.message}`);
            });
        }
        else {
            document.exitFullscreen();
        }
    };
    const handleDownload = () => {
        if (!blobUrl)
            return;
        fetch(blobUrl)
            .then((response) => response.blob())
            .then((blob) => {
            const blobUrlForDownload = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrlForDownload;
            const filename = fileUrl.split('/').pop() || 'document.pdf';
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrlForDownload);
        })
            .catch((error) => {
            console.error('Error downloading PDF:', error);
        });
    };
    const handleRotate = () => {
        setRotation((prev) => (prev + 90) % 360);
    };
    const togglePdfTheme = () => {
        setPdfTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
    };
    if (!fileUrl) {
        return (<div className="flex items-center justify-center h-full text-muted-foreground">{t("no_document_selected")}</div>);
    }
    if (isLoadingPdf || !blobUrl) {
        return (<div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"/>
          <p className="text-sm text-muted-foreground">{t("loading_pdf")}</p>
        </div>
      </div>);
    }
    if (loadError) {
        return (<div className="flex flex-col items-center justify-center h-full text-center p-6">
        <AlertCircle className="h-16 w-16 text-red-500 mb-4"/>
        <h2 className="text-xl font-semibold text-foreground mb-2">{t("failed_to_load_pdf")}</h2>
        <p className="text-muted-foreground mb-4 max-w-md">{loadError}</p>
        <Button onClick={() => {
                setLoadError(null);
                // Force reload by clearing blob URL
                if (blobUrlRef.current) {
                    URL.revokeObjectURL(blobUrlRef.current);
                    blobUrlRef.current = null;
                }
                setBlobUrl(null);
            }}>{t("retry")}</Button>
      </div>);
    }
    return (<div className="h-full w-full overflow-hidden" key="pdf-viewer-container">
      <div className="h-full rounded-lg border bg-gray-100 selection:text-transparent dark:bg-gray-800/50" ref={containerRef}>
        <Root source={blobUrl} className="flex h-full w-full flex-col" loader={<div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"/>
                <p className="text-sm text-muted-foreground">{t("loading_pdf")}</p>
              </div>
            </div>} onError={(error) => console.error('PDF load error:', error)} isZoomFitWidth zoomOptions={{
            minZoom: 0.25,
            maxZoom: 5,
        }}>
          {/* Toolbar */}
          <div className="flex items-center gap-2 rounded-t-lg border-b bg-gray-100 p-2 py-[4.5px] text-sm text-gray-600 dark:bg-gray-800/50 dark:text-gray-300">
            <button type="button" onClick={() => setShowThumbnails((show) => !show)} className="rounded-md px-2 py-1 hover:bg-gray-300 hover:text-gray-800/50 dark:hover:bg-gray-700 dark:hover:text-gray-100 transition-colors" title={t("toggle_thumbnails")}>
              {showThumbnails ? (<PanelRight className="h-4 w-4"/>) : (<PanelLeft className="h-4 w-4"/>)}
            </button>
            <button type="button" onClick={() => setShowSearch((show) => !show)} className="ml-[-8px] rounded-md px-2 py-1 hover:bg-gray-300 hover:text-gray-800/50 dark:hover:bg-gray-700 dark:hover:text-gray-100 transition-colors" title={t("toggle_search")}>
              <SearchIcon className="h-4 w-4"/>
            </button>
            <button type="button" onClick={togglePdfTheme} className="ml-[-8px] rounded-md px-2 py-1 hover:bg-gray-300 hover:text-gray-800/50 dark:hover:bg-gray-700 dark:hover:text-gray-100 transition-colors" title={`Switch PDF to ${pdfTheme === 'light' ? 'dark' : 'light'} theme`}>
              {pdfTheme === 'light' ? (<Moon className="h-4 w-4"/>) : (<Sun className="h-4 w-4"/>)}
            </button>
            <span className="flex-grow"/>
            <div className="flex items-center gap-1">
              <CustomPageInput className="rounded-lg border bg-white px-0 py-1 text-center dark:border-gray-700 dark:bg-gray-800"/>
              {' / '}
              <TotalPages />
            </div>
            <div className="ml-1.5 h-6 w-[1px] bg-gray-300 dark:bg-gray-700"/>
            <div className="flex items-center gap-1">
              <PDFZoomDropdown />
            </div>
            <span className="flex-grow"/>
            <div className="flex items-center gap-2">
              <button type="button" onClick={handleRotate} className="rounded-md p-1 hover:bg-gray-300 hover:text-gray-800/50 dark:hover:bg-gray-700 dark:hover:text-gray-100 transition-colors" title={t("rotate_90_u00b0_clockwise")}>
                <RotateCwSquare className="h-4 w-4"/>
              </button>
              <button type="button" onClick={handleDownload} className="rounded-md p-1 hover:bg-gray-300 hover:text-gray-800/50 dark:hover:bg-gray-700 dark:hover:text-gray-100 transition-colors" title={t("download_pdf")}>
                <Download className="h-4 w-4"/>
              </button>
              <button type="button" onClick={toggleFullScreen} className="rounded-md p-1 hover:bg-gray-300 hover:text-gray-800/50 dark:hover:bg-gray-700 dark:hover:text-gray-100 transition-colors" title={isFullscreen ? 'Exit Full Screen' : 'Full Screen'}>
                {isFullscreen ? (<Minimize className="h-4 w-4"/>) : (<Maximize className="h-4 w-4"/>)}
              </button>
            </div>
          </div>

          <PDFPagesWrapper showThumbnails={showThumbnails} showSearch={showSearch} rotation={rotation} pdfTheme={pdfTheme}/>
        </Root>
      </div>
    </div>);
};
export default PdfViewer;
